import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useMemoize } from '@vueuse/core'
import childEditionFlowActionsApi from '@/services/http/api/childEditionFlowActions'
import {
  FlowActionsChildEditionResponseSchema,
  UpdateFlowActionsChildEditionResponseSchema
} from '@/schemas/flowActions'
import type { FlowActionsChildEditionData, ChildEditionId } from '@/types'

export const useChildEditionFlowActionsStore = defineStore('childEditionFlowActionsStore', () => {
  // state
  const loader = ref<boolean>(false)
  const childEditionFlowActions = ref<FlowActionsChildEditionData | null>(null)

  // getters
  const canShowVolunteerCooperationSection = computed<boolean>(
    () => childEditionFlowActions.value?.canShowVolunteerCooperationSection ?? false
  )
  const canToggleVolunteerCooperation = computed<boolean>(
    () => childEditionFlowActions.value?.canToggleVolunteerCooperation ?? false
  )
  const canViewGiftGiverModuleLink = computed<boolean>(
    () => childEditionFlowActions.value?.canViewGiftGiverModuleLink ?? false
  )
  const canViewChildChoicePathLink = computed<boolean>(
    () => childEditionFlowActions.value?.canViewChildChoicePathLink ?? false
  )

  const memoizedGetFlowActions = useMemoize(childEditionFlowActionsApi.get)

  const fetchChildEditionFlowActions = async (childEditionId: ChildEditionId) => {
    loader.value = true
    try {
      const { data } = await memoizedGetFlowActions(childEditionId)
      const validated = FlowActionsChildEditionResponseSchema.parse(data)
      childEditionFlowActions.value = validated.data
      return validated.data
    } finally {
      loader.value = false
    }
  }

  const updateChildEditionFlowActionsFromBackendResponse = (data: unknown) => {
    const validatedData = UpdateFlowActionsChildEditionResponseSchema.parse(data)
    clearDataAndCache()
    childEditionFlowActions.value = validatedData.data.flow_actions
  }

  const clearData = () => {
    childEditionFlowActions.value = null
  }

  const clearDataAndCache = () => {
    clearData()
    memoizedGetFlowActions.clear()
  }

  return {
    // state
    loader,
    childEditionFlowActions,

    // getters
    canShowVolunteerCooperationSection,
    canToggleVolunteerCooperation,
    canViewGiftGiverModuleLink,
    canViewChildChoicePathLink,

    // actions
    fetchChildEditionFlowActions,
    updateChildEditionFlowActionsFromBackendResponse,
    clearData,
    clearDataAndCache
  }
})
